import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { getAuthenticatedUserId } from '@app/reducers/auth/auth.service';
import { SbFormFieldComponent } from '@app/shared/sb-lib/forms/sb-form-field.component';
import { SharedLaddaModule } from '@app/shared/shared-ladda.module';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppState } from '@reducers/index';
import { EmployeeService, getActiveEmployees } from '@reducers/orm/employee/employee.service';
import { TooltipDirective } from '@sb/tooltip';
import { ButtonComponent, InputModule, InputSelectDirective, SbDialogModule } from '@sb/ui';
import { take } from 'rxjs';

import { TranslationParamsPipe } from '../../../pipes/translation-params.pipe';
import { getVisibleTeams } from '../../../reducers/orm/team/team.service';
import { DynamicTableComponent } from '../dynamic-table/dynamic-table.component';
import { FirstErrorPipe } from '../onboarding-employees/first-error.pipe';

interface EmployeeFormData {
  id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  Team?: string;
  selected: boolean;
}

@Component({
  selector: 'employees-table',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    InputModule,
    SharedLaddaModule,
    SbDialogModule,
    DynamicTableComponent,
    SbFormFieldComponent,
    ButtonComponent,
    TooltipDirective,
    InputSelectDirective,
    TranslationParamsPipe,
    FirstErrorPipe,
  ],
  templateUrl: './employees-table.component.html',
})
export class EmployeesTableComponent implements OnInit {
  @ViewChildren('firstNameInput', { read: ElementRef })
  public firstNameInputs: QueryList<ElementRef<HTMLInputElement>>;

  @Input()
  public selectable = false;

  @Input()
  public showEmail = true;

  @Input()
  public form = new FormGroup({
    rows: new FormArray([]),
  });

  public get rows(): FormArray {
    return this.form.get('rows') as FormArray;
  }

  public get columns(): string[] {
    return this.showEmail
      ? [
          this.translate.instant('First name'),
          this.translate.instant('Last name'),
          this.translate.instant('Email'),
          this.translate.instant('Team'),
        ]
      : [this.translate.instant('First name'), this.translate.instant('Last name'), this.translate.instant('Team')];
  }

  public teams$ = this.store.select(getVisibleTeams(true));
  public isLoading = false;
  public readonly employeeLimit = 75; // Free account limit

  public get selectedRowsCount(): number {
    return this.form.value.rows?.reduce((acc, row) => acc + (row.selected ? 1 : 0), 0) || 0;
  }

  private authenticatedUserId: string;

  public constructor(
    private store: Store<AppState>,
    private employeeService: EmployeeService,
    private translate: TranslateService,
  ) {
    void this.store
      .select(getAuthenticatedUserId)
      .pipe(take(1))
      .subscribe((authenticatedUserId) => {
        this.authenticatedUserId = authenticatedUserId;
      });
  }

  public ngOnInit(): void {
    void this.store
      .select(getActiveEmployees)
      .pipe(take(1))
      .subscribe((employees) => {
        this.hydrateForm(employees);

        // Add an empty row by default if list is empty
        if (!this.rows.length) {
          this.addEmployee();
        }
      });
  }

  public addEmployee(): void {
    this.rows.push(this.getRowForm({ selected: this.selectable }));
    setTimeout(() => this.firstNameInputs.last.nativeElement.focus());
  }

  public removeEmployee(index: number): void {
    const employee = this.rows.at(index).value;
    if (employee.id) {
      void this.employeeService
        .remove(employee.id)
        .pipe(take(1))
        .subscribe(() => {
          this.rows.removeAt(index);
        });
    } else {
      this.rows.removeAt(index);
    }
  }

  public syncAllEmailsValidity() {
    if (!this.showEmail) return;
    for (let index = 0; index < this.rows.length; index++) {
      this.syncEmailValidity(index);
    }
  }

  public syncEmailValidity(index: number) {
    if (!this.showEmail) return;
    const row = this.rows.at(index);
    const emailControl = row.get('email');
    if (row.value.selected) {
      emailControl.setValidators([Validators.required, Validators.email]);
    } else {
      emailControl.setValidators(Validators.email);
    }
    emailControl.updateValueAndValidity();
  }

  private hydrateForm(employees): void {
    employees
      // Exclude authenticated user from the list
      .filter((employee) => employee.id !== this.authenticatedUserId)
      .forEach((employee) => {
        const { id, first_name: firstName, last_name: lastName, email, Team: teamIds } = employee;

        this.rows.push(
          this.getRowForm({
            id,
            firstName,
            lastName,
            email,
            Team: teamIds.length ? teamIds[0] : '',
            selected: !!email,
          }),
        );
      });
  }

  private getRowForm(data: EmployeeFormData) {
    const emailValidators = data.selected ? [Validators.required, Validators.email] : [Validators.email];
    // Only Team can be modified for authenticated user
    const disabled = data.id === this.authenticatedUserId;

    const controls = {
      first_name: new FormControl<string>({ value: data?.firstName || '', disabled }, Validators.required),
      last_name: new FormControl<string>({ value: data?.lastName || '', disabled }, Validators.required),
      email: new FormControl<string>({ value: data?.email || '', disabled }, emailValidators),
      Team: new FormControl<string>(data?.Team || '', Validators.required),
      removable: new FormControl<boolean>(!disabled),
    };

    if (data?.id) {
      controls['id'] = new FormControl<string>(data.id);
    }

    if (this.selectable) {
      controls['selected'] = new FormControl<boolean>(data.selected);
    }

    return new FormGroup(controls);
  }
}
